import type { ReactNode } from 'react'
import { useTheme } from 'styled-components'
import { FAIcon } from '@/components/fa-icon'
import type { FAIconProps } from '@/components/fa-icon'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { useTooltipTap } from '@/components/use-tooltip-tap'
import { ZIndexRange } from '@/types/style'

/**
 * How much an approver needs to look at the flag. Decides the icon's colour,
 * which flag represents the row when several are collapsed, and the order they
 * are listed in.
 */
export type WorkPeriodFlagSeverity = 'high' | 'medium' | 'low'

export interface WorkPeriodFlag {
  key: string
  icon: FAIconProps['icon']
  severity: WorkPeriodFlagSeverity
  /** Shown on its own as the tooltip, or as one entry of the collapsed menu. */
  label: ReactNode
}

const SEVERITY_RANK: Record<WorkPeriodFlagSeverity, number> = {
  high: 0,
  medium: 1,
  low: 2,
}

/**
 * Colour carries the severity, so an approver reads the row's worst problem off
 * the icon without opening anything. The mobile cards list the same flags as
 * labelled lines and take their colours from here too.
 */
export function useWorkPeriodFlagColors(): Record<
  WorkPeriodFlagSeverity,
  string
> {
  const theme = useTheme()
  return {
    high: theme.colors.red,
    medium: theme.colors.orange,
    low: theme.colors.primary,
  }
}

interface Props {
  flags: WorkPeriodFlag[]
}

/**
 * The row's flags as a single icon. A work day can pick up any number of these
 * and a row of six icons says less than one does, so only the most severe is
 * drawn, with a count beside it; the rest are one hover (or tap) away.
 */
export function WorkPeriodFlags({ flags }: Props) {
  const { rootProps, triggerProps, contentProps } = useTooltipTap()
  const severityColor = useWorkPeriodFlagColors()

  if (flags.length === 0) return null

  const ordered = [...flags].sort(
    (a, b) => SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity]
  )
  const [worst] = ordered
  if (!worst) return null

  return (
    <Tooltip {...rootProps}>
      <TooltipTrigger
        {...triggerProps}
        type="button"
        className="inline-flex items-center gap-0.5 align-middle"
      >
        <FAIcon
          icon={worst.icon}
          style={{ color: severityColor[worst.severity] }}
        />
        {ordered.length > 1 && (
          <span className="inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-stone-200 px-1 text-[0.65rem] leading-none font-semibold text-stone-700">
            {ordered.length}
          </span>
        )}
      </TooltipTrigger>

      <TooltipContent
        {...contentProps}
        className="max-w-sm text-left text-wrap"
        style={{ zIndex: ZIndexRange.Extra }}
      >
        <ul className="flex flex-col gap-2">
          {ordered.map(flag => (
            <li key={flag.key} className="flex items-start gap-2">
              <FAIcon
                icon={flag.icon}
                fixedWidth
                className="mt-0.5"
                style={{ color: severityColor[flag.severity] }}
              />
              <div className="min-w-0">{flag.label}</div>
            </li>
          ))}
        </ul>
      </TooltipContent>
    </Tooltip>
  )
}
